import { CrudOperators, LogicalFilter } from "@refinedev/core";
import { createStore } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type LabelInfo = {
  descriptionKey: string;
  value?: string;
  descriptionSuffixKey?: string;
  isValueTranslated?: boolean;
  isValueDate?: boolean;
};

export type LabeledFilter = LogicalFilter & {
  labelInfo?: LabelInfo;
};

type State = {
  filters: Record<string, LabeledFilter>;
};

type Action = {
  setFilter: (_filter: LabeledFilter) => void;
  deleteFilter: (_field: string, _operator: CrudOperators) => void;
  clearFilters: () => void;
};

export type FilterStore = ReturnType<typeof createFilterStore>;
export const defaultFilterStores = new Map<string, FilterStore>();

// Creates a filter store with the given initial filters and key
// Each unique key is a unique store for an inventory tab
// The key is used to identify the store in local storage
// Uses zustand middleware to persist the store in local storage
const createFilterStore = (
  initialFilters: Record<string, LabeledFilter>,
  filterKey: string,
) => {
  return createStore<State & Action, [["zustand/persist", unknown]]>(
    persist(
      (set) => ({
        filters: initialFilters,
        setFilter: (filter) => {
          set((state) => {
            return {
              filters: {
                ...state.filters,
                [filter.field + filter.operator]: filter,
              },
            };
          });
        },
        deleteFilter: (field: string, operator: CrudOperators) => {
          set((state) => {
            const filters = { ...state.filters };
            delete filters[field + operator];
            return { filters };
          });
        },
        clearFilters: () => {
          set({ filters: initialFilters });
        },
      }),
      {
        name: filterKey,
        storage: createJSONStorage(() => localStorage),
      },
    ),
  );
};

// creates or fetches a filter store based on the filterKey and sets initial filters
// if the store does not exist, it is created and stored in the defaultFilterStores map
const createFilterStoreFactory = (filterStores: typeof defaultFilterStores) => {
  return (
    initialFilters: Record<string, LabeledFilter>,
    keyPrefix: string,
    keySuffix: string,
  ) => {
    const filterKey = getFilterKey(keyPrefix, keySuffix);
    if (!filterStores.has(filterKey)) {
      filterStores.set(filterKey, createFilterStore(initialFilters, filterKey));
    }

    return filterStores.get(filterKey)!;
  };
};

const getFilterKey = (keyPrefix: string, keySuffix: string) =>
  `${keyPrefix}-filter-store-${keySuffix}`;

export const getFilterStore = createFilterStoreFactory(defaultFilterStores);
